import { z } from "zod";

export function hrefTelNumber(input: string): string | undefined {
    const schema = z
        .string()
        .regex(/^[\d-().+ ]*$/, "Invalid characters in phone number")
        .transform(
            val => val.replace(/[^\d]/g, ""), // Remove all non-digit characters
        )
        .refine(val => val.length === 10 || val.length === 11, {
            message: "Phone number must be 10 or 11 digits",
        })
        .transform(val => {
            // Ensure it starts with 1 if 11 digits, otherwise use as-is
            const cleaned = val.length === 11 ? val.slice(1) : val;
            return `tel:${cleaned}`;
        });

    try {
        return schema.parse(input);
    } catch {
        return undefined;
    }
}

export function validEmail(input: string): string | undefined {
    try {
        return z.string().email("Invalid email address").parse(input);
    } catch {
        return undefined;
    }
}

/**
 * Supports a wide range of email list variations
 * Comma separated, space separated, newline separated, or all of the above because we normalize first
 */
export function validateEmailListString(input: string): {
    validEmails: string[];
    errorMessage?: string;
} {
    const validEmailsSet = new Set<string>();
    const invalidEmailsSet = new Set<string>();
    const normalized = input
        .toLowerCase()
        .trim()
        .replace(/[\n\r\s]+/g, ",") // Replace whitespaces with a comma
        .replace(/,+/g, ",") // Replace multiple commas with single comma
        .replace(/^,|,$/g, ""); // Remove leading/trailing commas

    normalized.split(",").forEach(part => {
        if (part === "") return;

        const valid = validEmail(part);
        if (valid) {
            validEmailsSet.add(valid);
        } else {
            invalidEmailsSet.add(part);
        }
    });
    const validEmails = Array.from(validEmailsSet);

    const errorMessage = invalidEmailsSet.size > 0 ? `Invalid Emails: ${Array.from(invalidEmailsSet).join(", ")}` : undefined;

    return {
        validEmails,
        errorMessage,
    };
}

/**
 * Takes anything as a parameter and attempts to parse it as a number. In the event that it fails it will return
 * the default value. This function does not throw.
 *
 * Handled cases resulting in the defaultValue:
 * - undefined, null, NaN, anything else
 */
export function numberOrDefault(value: any, defaultValue: number) {
    return z.coerce // coerce takes care of most cases
        .number()
        .catch(defaultValue) // instead of the .parse throwing, just return the defaultValue
        .parse(value);
}

export function formatCurrency(total: number | string | null | undefined, currencyCode: string | undefined | null): string | undefined {
    if (!total) return undefined;

    const validCurrencyCode = z.coerce
        .string()
        .refine(val => {
            // this will throw if false is returned
            return Intl.supportedValuesOf("currency").includes(val);
        })
        .optional()
        .catch(undefined) // so default to undefined
        .parse(currencyCode);

    try {
        const validTotal = z.coerce
            .number()
            .positive() // reject 0, negative numbers
            .parse(total);

        // If we have a valid code let Intl format it
        if (validCurrencyCode) {
            return Intl.NumberFormat("en-US", { style: "currency", currency: validCurrencyCode }).format(validTotal);
        }

        // If not, return toFixed for a valid number
        return validTotal.toFixed(2);
    } catch (e) {
        // Otherwise we failed to get a valid number or Intl threw when formatting it so just
        // return undefined and let the UI handle the non-existence of the currency val
        console.warn(`Could not convert the total: ${total} with code: ${currencyCode} to valid currency`, e);
        return undefined;
    }
}

const ZipPath = z
    .string()
    .min(1)
    .max(500)
    .transform(path =>
        path
            .replaceAll("\\", "/") // windows separators → slashes first
            .replaceAll(" ", "_") // spaces → _ so raw DB names just work
            .split("/")
            // Drop what can't be a real segment: empties (leading/trailing/double
            // slashes), "." (noop), ".." (parent escape). Anything left is a plain
            // relative path by construction — no reject branch needed for these.
            .filter(seg => seg !== "" && seg !== "." && seg !== "..")
            .join("/")
    )
    // Only failure left: input that was nothing-but-garbage ("../..", "/", "")
    // cleans to empty, which min(1) would fail
    .pipe(z.string().min(1));

const ZipEntrySchema = z.object({
    path: ZipPath,
    fileUploadId: z.number().int().positive()
});

export const ValidationSchemas = {
    ZipPath,
    ZipEntrySchema
}

/**
 * **NOTE**: Copied from the API
 *
 * Checks a password to ensure that it meets the complexity requirement of:
 * - Uppercase and lowercase letters
 * - At least one number
 * - At least one non-alphanumeric char
 * - At least 8 characters long
 */
export function CheckPasswordStrength(password: string) {
    return (
        /[a-z]/.test(password) &&
        /[A-Z]/.test(password) &&
        /[0-9]/.test(password) &&
        /[^A-Za-z0-9]/.test(password) &&
        password.length > 7
    );
}

